import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import {
  CustomButton,
  CustomModal,
  useScrollToFirstError,
} from "@ippon/shared-components";
import type {
  AreaType,
  RevenueType,
  OccupancyStatus,
  PrivateDetail,
  CommonDetail,
  FacilityDetail,
  LandDetail,
  BudgetInfo,
  StockRevenueInfo,
  ContractInfo,
  CustomFieldValue,
  UnitCustomFieldSection,
} from "@ippon/shared-types";
import type { AppDispatch } from "@/store";
import { createUnitCustomField } from "@/store/unitCustomFieldSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import BasicSection from "./sections/BasicSection";
import DetailSection from "./sections/DetailSection";
// 予算は予実管理 (Budget タブ) のみで管理する方針に変更したため、
// RevenueSection (区画ごとの月額予算入力) は import しない。
// コンポーネント自体は将来の再導入に備えて残す。
import ContractSection from "./sections/ContractSection";
// 旧 CustomFieldsSection (一括「カスタムフィールド」セクション表示) は廃止。
// 各セクション (BasicSection / DetailSection) の末尾に
// section_key 別で UnitSectionCustomFields を埋め込む形に移行。
// 関連資料 / 区画画像は DetailSection 内部で各 *_detail に内包する。

/**
 * UnitTabForm の onSubmit に渡される値。
 * 区画情報 (基本) の直下フィールドのうち UnitFormValues に乗っていないものを補完する。
 * `__pending_custom_fields` は form-state-only (submit 時に flush されて strip される)。
 */
export type UnitFormSubmitValues = Omit<UnitFormValues, "__pending_custom_fields"> & {
  floor_plan?: string;
  is_parking?: boolean;
};

/**
 * カスタム項目の未保存追加分。
 * 「+項目を追加」で UI に出現するが、まだ master に登録されていないラベル。
 * submit 時に `createUnitCustomField` で master に upsert → 戻ってきた field_id を
 * `custom_fields` にマージしてから record save する。
 */
export interface PendingCustomField {
  temp_id: string;
  label: string;
  section_key: UnitCustomFieldSection;
  value: string;
}

export interface UnitFormValues {
  area_type: AreaType;
  landmark_name: string;
  occupancy_status: OccupancyStatus;
  display_order?: number;
  revenue_type: RevenueType;
  /** 収益項目 (付帯=stock_other のとき必須)。BudgetCategory.category_id */
  revenue_item_category_id?: string;
  notes?: string;
  manager_employee_id?: string;
  // 場所区分別の詳細情報 (area_type に応じてどれか1つを編集)
  private_detail?: PrivateDetail;
  common_detail?: CommonDetail;
  facility_detail?: FacilityDetail;
  land_detail?: LandDetail;
  budget?: BudgetInfo;
  revenue?: StockRevenueInfo;
  contract?: ContractInfo;
  custom_fields?: CustomFieldValue[];
  /** 未保存の追加カスタム項目 (submit 時に flush) */
  __pending_custom_fields?: PendingCustomField[];
}

/** onSubmit に渡すフォームヘルパー。API のフィールド単位エラーを input 下に出すのに使う。 */
export interface UnitFormSubmitHelpers {
  setError: (name: any, error: { type?: string; message: string }) => void;
  setFocus: (name: any) => void;
}

interface Props {
  propertyId: string;
  initialValues?: Partial<UnitFormSubmitValues>;
  onSubmit: (
    values: UnitFormSubmitValues,
    helpers: UnitFormSubmitHelpers,
  ) => Promise<void> | void;
  onCancel?: () => void;
  submitLabel?: string;
  saving?: boolean;
  hideActionBar?: boolean;
  formId?: string;
  /** 親モーダルが「未入力 disabled」を判定するための dirty 通知 */
  onDirtyChange?: (isDirty: boolean) => void;
  /** 区画詳細ページ等で BasicSection 内のセクションタイトルを抑止 */
  hideBasicTitle?: boolean;
  /**
   * 場所区分セレクトを無効化 (区画詳細では変更不可・グレーアウト)。
   * 新規登録モーダル等では未指定 (false)。
   */
  disableAreaTypeSelect?: boolean;
  /**
   * 契約者情報セクションを非表示にする (区画詳細ページ向け)。
   * 区画詳細では契約系はテナント管理タブの責務なのでここでは編集させない。
   */
  omitContract?: boolean;
  /**
   * 物件詳細・区画詳細など固定幅レイアウトに合わせるためのラベル/入力幅。
   * 未指定時は各 Input コンポーネントのデフォルト幅を使う (登録モーダル想定)。
   */
  labelWidth?: string;
  inputWidth?: string;
}

const buildDefaults = (
  override: Partial<UnitFormValues> = {},
): UnitFormValues => {
  return {
    // 未指定時は未選択 (空) で開始する。required バリデーションが拾うので AreaType 型は維持。
    area_type: (override.area_type ?? "") as AreaType,
    landmark_name: override.landmark_name ?? "",
    occupancy_status: override.occupancy_status ?? "vacant",
    display_order: override.display_order,
    revenue_type: override.revenue_type ?? "none",
    revenue_item_category_id: override.revenue_item_category_id,
    notes: override.notes,
    manager_employee_id: override.manager_employee_id,
    private_detail: override.private_detail,
    common_detail: override.common_detail,
    facility_detail: override.facility_detail,
    land_detail: override.land_detail,
    budget: override.budget,
    revenue: override.revenue,
    contract: override.contract,
    custom_fields: override.custom_fields,
    __pending_custom_fields: [],
  };
};

const normalizeUnitCustomFieldValue = (value: CustomFieldValue["value"]) => {
  if (value == null) return "";
  if (Array.isArray(value)) return [...value].sort();
  return String(value);
};

const isEmptyUnitCustomFieldValue = (
  value: ReturnType<typeof normalizeUnitCustomFieldValue>,
) => {
  if (Array.isArray(value)) return value.length === 0;
  return value === "";
};

const serializeUnitCustomFields = (values: UnitFormValues) => {
  const customFieldMap = new Map<string, CustomFieldValue["value"]>();
  (values.custom_fields || []).forEach((v) => {
    customFieldMap.set(v.field_id, v.value);
  });
  Object.entries(values as unknown as Record<string, unknown>).forEach(
    ([key, value]) => {
      if (!key.startsWith("__cf_")) return;
      customFieldMap.set(
        key.slice("__cf_".length),
        value as CustomFieldValue["value"],
      );
    },
  );

  return JSON.stringify({
    custom_fields: Array.from(customFieldMap.entries())
      .map((v) => ({
        field_id: v[0],
        value: normalizeUnitCustomFieldValue(v[1]),
      }))
      .filter((v) => !isEmptyUnitCustomFieldValue(v.value))
      .sort((a, b) => a.field_id.localeCompare(b.field_id)),
    __pending_custom_fields: (values.__pending_custom_fields || [])
      .map((v) => ({
        temp_id: v.temp_id,
        label: v.label.trim(),
        section_key: v.section_key,
        value: v.value,
      }))
      .filter((v) => v.label || v.value)
      .sort((a, b) => a.temp_id.localeCompare(b.temp_id)),
  });
};

const UnitTabForm: React.FC<Props> = ({
  propertyId,
  initialValues,
  onSubmit,
  onCancel,
  submitLabel = "保存",
  saving,
  hideActionBar,
  formId,
  onDirtyChange,
  hideBasicTitle,
  disableAreaTypeSelect,
  omitContract,
  labelWidth,
  inputWidth,
}) => {
  const form = useForm<UnitFormValues>({
    defaultValues: buildDefaults(initialValues),
  });
  const { watch, handleSubmit, formState } = form;
  const watchedValues = watch();
  const initialCustomFieldsSnapshotRef = useRef(
    serializeUnitCustomFields(buildDefaults(initialValues)),
  );
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();
  const scrollToFirstError = useScrollToFirstError<UnitFormValues>();
  const hasNonCustomDirty = Object.keys(formState.dirtyFields).some(
    (key) =>
      key !== "custom_fields" &&
      key !== "__pending_custom_fields" &&
      !key.startsWith("__cf_") &&
      !key.startsWith("__month_picker_"),
  );
  const customFieldsDirty =
    serializeUnitCustomFields(watchedValues) !==
    initialCustomFieldsSnapshotRef.current;
  const isDirty = hasNonCustomDirty || customFieldsDirty;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const revenueType = watch("revenue_type");
  const occupancyStatus = watch("occupancy_status");

  // 新仕様の場所区分別セクション可視性。
  // - 詳細情報セクション: 常時表示 (中身は DetailSection が areaType で分岐)。
  //   関連資料・区画画像も DetailSection の中で各 *_detail 配下に内包する。
  // - 契約セクションは旧来通り 収益=stock かつ 入居中 で表示 (テナント管理連携)
  const visible = useMemo(
    () => ({
      contract:
        !omitContract &&
        (revenueType === "stock_rent" || revenueType === "stock_other") &&
        occupancyStatus === "occupied",
    }),
    [revenueType, occupancyStatus, omitContract],
  );

  // 「収益あり (stock) → 収益なし (none)」を更新時に検知して確認モーダルを出すため、
  // 初期 (= 既存) の収益区分を保持する。確定後は現在値に更新して再発火を防ぐ。
  const initialRevenueRef = useRef<string>(
    initialValues?.revenue_type ?? "none",
  );
  const [pendingNoneConfirm, setPendingNoneConfirm] = useState(false);
  const pendingValuesRef = useRef<UnitFormValues | null>(null);

  // 実際の保存処理 (custom field flush → onSubmit)。確認モーダル確定後にも使う。
  const finalizeSubmit = useCallback(
    async (v: UnitFormValues) => {
      // pending fields を flush: 各 label を master に upsert → 戻りの
      // field_id を custom_fields にマージしてから record save する。
      const pending = v.__pending_custom_fields ?? [];
      let mergedCustomFields = v.custom_fields ?? [];
      if (pending.length > 0) {
        const results = await Promise.all(
          pending.map((p) =>
            dispatch(
              createUnitCustomField({
                label: p.label,
                field_type: "text",
                section_key: p.section_key,
              }),
            ),
          ),
        );
        const failed = results.find(
          (r) => !createUnitCustomField.fulfilled.match(r),
        );
        if (failed) {
          const msg =
            (failed.payload as string) ||
            "カスタムフォーム項目の追加に失敗しました";
          showToast({ type: "error", message: msg });
          return;
        }
        const newValues: CustomFieldValue[] = results.map((r, i) => ({
          field_id: (r.payload as { field_id: string }).field_id,
          value: pending[i].value,
        }));
        mergedCustomFields = [...mergedCustomFields, ...newValues];
      }
      // 不要な __pending_custom_fields は親に渡さない
      const { __pending_custom_fields: _drop, ...rest } = v;
      void _drop;
      const submitValues = {
        ...rest,
        custom_fields: mergedCustomFields,
        // 付帯 (stock_other) 以外では収益項目を送らない (BE 側でもクリアされるが FE でも整える)
        revenue_item_category_id:
          rest.revenue_type === "stock_other"
            ? rest.revenue_item_category_id
            : undefined,
      } as UnitFormSubmitValues;
      await onSubmit(submitValues, {
        setError: form.setError,
        setFocus: form.setFocus,
      });
      // 以降の再 submit で誤発火しないよう初期収益区分を現在値に更新
      initialRevenueRef.current = v.revenue_type;
      // submit 成功後は pending をクリア (モーダルが残るケースに備える)
      form.setValue("__pending_custom_fields", []);
      initialCustomFieldsSnapshotRef.current = serializeUnitCustomFields({
        ...v,
        custom_fields: mergedCustomFields,
        __pending_custom_fields: [],
      });
    },
    [dispatch, showToast, onSubmit, form],
  );

  // 収益あり → なし への変更を伴う submit は確認モーダルを挟む。
  const handleValidSubmit = useCallback(
    async (v: UnitFormValues) => {
      const wasStock =
        initialRevenueRef.current === "stock_rent" ||
        initialRevenueRef.current === "stock_other";
      if (wasStock && v.revenue_type === "none") {
        pendingValuesRef.current = v;
        setPendingNoneConfirm(true);
        return;
      }
      await finalizeSubmit(v);
    },
    [finalizeSubmit],
  );

  const handleConfirmRevenueToNone = useCallback(async () => {
    setPendingNoneConfirm(false);
    const v = pendingValuesRef.current;
    pendingValuesRef.current = null;
    if (v) await finalizeSubmit(v);
  }, [finalizeSubmit]);

  return (
    <Box
      component="form"
      id={formId}
      onSubmit={(e) => {
        // モーダル経由で利用される際、submit イベントが React ツリーを
        // 伝播して親 PropertyOverview の form (onUpdateProperty) を
        // 二重発火させてしまうため、ここで止める
        e.stopPropagation();
        void handleSubmit(handleValidSubmit, scrollToFirstError)(e);
      }}
      sx={{ pb: hideActionBar ? 0 : 12 }}
    >
      <Stack spacing={2}>
        <BasicSection
          form={form}
          propertyId={propertyId}
          hideTitle={hideBasicTitle}
          disableAreaTypeSelect={disableAreaTypeSelect}
          labelWidth={labelWidth}
          inputWidth={inputWidth}
        />
        {/* 予算セクションは予実管理タブで一元管理する方針のため削除済 */}
        <DetailSection
          form={form}
          labelWidth={labelWidth}
          inputWidth={inputWidth}
        />
        {visible.contract && (
          <ContractSection
            form={form}
            showVehicleFields={false}
            isAncillary={revenueType === "stock_other"}
          />
        )}
      </Stack>

      {!hideActionBar && (
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "white",
            borderTop: "1px solid #E0E0E0",
            py: 1.5,
            px: 2,
            mt: 3,
            zIndex: 5,
          }}
        >
          <Stack direction="row" spacing={1.5} justifyContent="flex-end">
            {onCancel && (
              <CustomButton
                label="キャンセル"
                buttonCategory="cancel"
                onClick={onCancel}
              />
            )}
            <CustomButton
              label={submitLabel}
              buttonCategory="submit"
              type="submit"
              isLoading={saving}
              disabled={saving}
            />
          </Stack>
        </Box>
      )}

      {/* 収益あり → なし への変更を伴う更新時の確認モーダル。
          確定すると BE 側で当区画の予算・実績・テナント・入金・支出を物理削除する。 */}
      <CustomModal
        openModal={pendingNoneConfirm}
        handleCloseModal={() => {
          setPendingNoneConfirm(false);
          pendingValuesRef.current = null;
        }}
        title="収益カテゴリを変更しますか？"
        modalType="deleteModal"
        bodyText=""
        hideCloseButton
      >
        <Box>
          <Typography sx={{ fontSize: 14, color: "#3E3E3E" }}>
            「収益あり」から「収益なし」に変更すると、この区画に紐づく
            <strong>予算</strong>、<strong>実績</strong>、
            <strong>テナント情報</strong>、<strong>入金</strong>、
            <strong>支出</strong>のすべてが削除されます。
            <br />
            この操作は取り消せません。よろしいですか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={() => {
              setPendingNoneConfirm(false);
              pendingValuesRef.current = null;
            }}
            buttonCategory="cancelModal"
          />
          <CustomButton
            label="変更する"
            onClick={handleConfirmRevenueToNone}
            buttonCategory="deleteModal"
          />
        </Box>
      </CustomModal>
    </Box>
  );
};

export default UnitTabForm;
